import * as React from 'react';
import Stepper from '@mui/joy/Stepper';
import Step, {stepClasses} from '@mui/joy/Step';
import StepIndicator, {stepIndicatorClasses} from '@mui/joy/StepIndicator';
import {Stack, SxProps, Typography} from "@mui/material";
import {Button, ColorPaletteProp, ColorPalettePropOverrides, StepButton, useTheme} from "@mui/joy";
import {CheckOutlined} from "@mui/icons-material";
import {Dayjs} from "dayjs";
import {ToDateTimeDefault} from "@shared/MathHelp.ts";
import {UsuarioTypo} from "@components/usuario/UsuarioTypo.tsx";

type stepVariant = 'solid' | 'soft';

// Define a interface para a configuração de cada passo
export interface StepConfig {
    icon?: React.ReactElement;
    fulfilled?: boolean;
    disabled?: boolean;
    error?: boolean;
    label?: string;
    fulfilledLabel?: string;
    variant: stepVariant;
    hidden?: boolean;
    color?: ColorPaletteProp | ColorPalettePropOverrides
    onClick?: () => void;
    time?: Dayjs | string
    usuarioId?: number
}

// Define a interface para as props do nosso componente
export interface IconStepperProps {
    steps: StepConfig[];
    orientation?: 'horizontal' | 'vertical';
    size?: 'sm' | 'md' | 'lg';
    sx?: SxProps;
    disabled?: boolean;
}

const hexToRgb = (hex: string): string | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : null;
};

const IconStepper: React.FC<IconStepperProps> = ({
                                                     steps,
                                                     orientation = 'horizontal',
                                                     size = 'lg',
                                                     sx = {},
                                                     disabled = false
                                                 }) => {
    const theme = useTheme();
    // Estilos base derivados do seu componente original
    const baseSx: SxProps = {
        width: '100%',
        '--StepIndicator-size': {lg: '3rem', md: '2.5rem', sm: '2rem'}[size],
        '--Step-connectorThickness': '3px',
        '--Step-connectorInset': '-1.5rem',
        '--Step-gap': '8px',
        [`& .${stepIndicatorClasses.root}`]: {
            borderWidth: 4,
        },
        [`& .${stepClasses.root}::after`]: {
            // height: 4,
        },
        [`& .${stepClasses.completed}`]: {
            [`& .${stepIndicatorClasses.root}`]: {
                borderColor: 'currentColor',
            },
            '&::after': {
                bgcolor: 'primary.300',
            },
        },
        [`& .${stepClasses.active}`]: {
            [`& .${stepIndicatorClasses.root}`]: {
                borderColor: 'currentColor',
            },
        },
        [`& .${stepClasses.disabled} *`]: {
            color: 'neutral.outlinedDisabledColor',
        },
    }

    return (
        <>
            <Stepper size={size} orientation={orientation} sx={{...baseSx, ...sx}}>
                {steps.map((step, index) => {
                    const isClickable = disabled ? false : step.onClick && !step.fulfilled && !step.disabled;
                    const activeColorName = step.error ? 'danger' : step.color ? step.color : 'primary';
                    // @ts-ignore
                    const colorHex = theme.palette[activeColorName]?.[500] ?? theme.palette.primary[500];
                    const colorChannels = hexToRgb(colorHex);

                    // 1. Criar um nome de animação único para cada passo
                    const animationName = `pulse-animation-${activeColorName}`;

                    const pulseKeyframes = {
                        // 2. Usar o nome único na definição do @keyframes
                        [`@keyframes ${animationName}`]: {
                            '0%': {
                                transform: 'scale(0.95)',
                                boxShadow: `0 0 0 0 rgba(${colorChannels}, 0.7)`,
                            },
                            '70%': {
                                transform: 'scale(1)',
                                boxShadow: `0 0 0 10px rgba(${colorChannels}, 0)`,
                            },
                            '100%': {
                                transform: 'scale(0.95)',
                                boxShadow: `0 0 0 0 rgba(${colorChannels}, 0)`,
                            },
                        },
                    };
                    if (step.hidden) {
                        return null; // Ignorar passos ocultos
                    }


                    return (
                        <Step
                            key={index}
                            orientation={'vertical'}
                            completed={step.variant === 'solid'}
                            active={step.variant === 'soft'}
                            indicator={
                                <StepIndicator
                                    variant={step.variant === 'solid' ? 'solid' : 'soft'}
                                    // @ts-ignore
                                    color={step.error ? 'danger' : step.color ? step.color : (step.disabled && !step.fulfilled) ? 'neutral' : step.fulfilled ? 'success' : 'primary'
                                    }
                                >
                                    {isClickable ? (
                                        <StepButton
                                            onClick={step.onClick}
                                            sx={{
                                                ...pulseKeyframes,
                                                background: 'transparent',
                                                border: 'solid',
                                                borderColor: `${activeColorName}.700`,
                                                padding: 0,
                                                margin: 0,
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                borderRadius: '50%',
                                                // 3. Aplicar a animação com o nome único
                                                animation: `${animationName} 2s infinite`,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                                    borderColor: 'info',
                                                },
                                            }}
                                        >
                                            {step.icon && step.icon || (step.fulfilled ? <CheckOutlined/> :
                                                <Typography>{index + 1}</Typography>)}
                                        </StepButton>
                                    ) : step.icon && step.icon || (step.fulfilled ?
                                        <CheckOutlined/> : index + 1)}
                                </StepIndicator>
                            }
                        >
                            {step.label && (<Stack>
                                    <Button variant={'plain'}
                                        //@ts-ignore
                                            color={step.error ? 'error' : step.color}
                                            disabled={!isClickable || step.disabled}
                                            onClick={() => {
                                                if (isClickable && step.onClick) step.onClick()
                                            }}
                                            sx={{
                                                textTransform: 'uppercase',
                                                fontWeight: 'lg',
                                                fontSize: '0.75rem',
                                                letterSpacing: '0.5px',
                                                textDecoration: isClickable ? 'underline' : 'none',
                                            }}
                                    >
                                        {step.fulfilledLabel != null && step.fulfilled ? step.fulfilledLabel : step.label}

                                    </Button>
                                    {step.usuarioId &&
                                        <UsuarioTypo usuarioId={step.usuarioId} typographyProps={{
                                            color: 'textDisabled', sx: {
                                                textAlign: 'center',
                                                fontSize: '0.65rem',
                                            }
                                        }}/>}
                                    {step.time && <Typography textAlign={'center'} color={'textDisabled'} sx={{
                                        fontSize: '0.65rem',

                                    }}>{ToDateTimeDefault(step.time)}</Typography>}

                                </Stack>
                            )}
                        </Step>
                    );
                })}
            </Stepper>
        </>
    );
};

export default IconStepper;